import {
  DashboardOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  AuditOutlined,
  FileExclamationOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  BuildOutlined,
  ContactsOutlined,
  TeamOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { isPresupuestosEntryContext } from 'utils/entryContext';
import { readStoredAuthUser } from 'utils/authSession';

const icons = {
  DashboardOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  AuditOutlined,
  FileExclamationOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  BuildOutlined,
  ContactsOutlined,
  TeamOutlined,
  PieChartOutlined
};

function normalizePathname(pathname) {
  if (!pathname) return '';
  const baseName = import.meta.env?.VITE_APP_BASE_NAME || '';
  let normalized = pathname;
  if (baseName && normalized.startsWith(baseName)) {
    normalized = normalized.slice(baseName.length) || '/';
  }
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  return normalized;
}

function getAreaBase() {
  try {
    const p = typeof window !== 'undefined' ? normalizePathname(window.location?.pathname || '') : '';
    if (p.startsWith('/admin')) return '/admin';
    if (p.startsWith('/contabilidad')) return '/contabilidad';
    if (p.startsWith('/produccion')) return '/produccion';
    if (p.startsWith('/areas/agenda')) return '/areas/agenda';
    if (p.startsWith('/areas/mantenimientos-periodicos')) return '/areas/mantenimientos-periodicos';
    if (p.startsWith('/areas/instrumentacion')) return '/areas/instrumentacion';
    if (p.startsWith('/areas/iso')) return '/areas/iso';
    if (p.startsWith('/areas/reportes')) return '/areas/reportes';
    if (p.startsWith('/areas/presupuestos')) return '/areas/presupuestos';
    if (p.startsWith('/areas/plan-estrategico')) return '/areas/plan-estrategico';
    if (p.startsWith('/areas/portal-usuario')) return '/areas/portal-usuario';
    if (p.startsWith('/cuadros')) return '/cuadros';
    if (p.startsWith('/operarios')) return '/areas';
    if (p.startsWith('/familias/')) {
      const [, , slug] = p.split('/');
      if (slug) return `/familias/${slug}`;
    }
    if (p.startsWith('/i')) return '/i';
    if (p.startsWith('/v')) return '/v';
  } catch {}
  try {
    const a = localStorage.getItem('current_area');
    if (a === 'contabilidad') return '/contabilidad';
    if (a === 'produccion') return '/produccion';
    if (a === 'agenda') return '/areas/agenda';
    if (a === 'mantenimientos-periodicos') return '/areas/mantenimientos-periodicos';
    if (a === 'instrumentacion') return '/areas/instrumentacion';
    if (a === 'iso') return '/areas/iso';
    if (a === 'reportes') return '/areas/reportes';
    if (a === 'presupuestos') return '/areas/presupuestos';
    if (a === 'plan-estrategico') return '/areas/plan-estrategico';
    if (a === 'portal-usuario') return '/areas/portal-usuario';
    if (a === 'operarios') return '/areas';
    if (a === 'vrp-cuadros') return '/cuadros';
    if (a && a.startsWith('familia-')) {
      const slug = a.slice('familia-'.length).toLowerCase();
      if (slug) return `/familias/${slug}`;
    }
    if (a === 'v') return '/v';
    if (a === 'i') return '/i';
  } catch {}
  return '';
}

const getStoredUser = () => {
  return readStoredAuthUser();
};

const isAdminUser = () => (getStoredUser()?.role || '').toString().trim().toLowerCase() === 'admin';
const OPERARIOS_ROLES = new Set(['admin', 'supervisor', 'produccion']);

const hasOperariosAccess = () => OPERARIOS_ROLES.has((getStoredUser()?.role || '').toString().trim().toLowerCase());

const isOperariosContext = () => {
  try {
    const p = typeof window !== 'undefined' ? normalizePathname(window.location?.pathname || '') : '';
    if (p.startsWith('/operarios')) return true;
  } catch {}
  try {
    const area = localStorage.getItem('current_area');
    const path = typeof window !== 'undefined' ? normalizePathname(window.location?.pathname || '') : '';
    return area === 'operarios' && !path.startsWith('/areas');
  } catch {
    return false;
  }
};

const GESTION_PROYECTOS_SUBAREA_SLUG = 'gestion-proyectos';

const getStoredAllowedAreas = () => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem('vrp_allowed_areas') : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const hasGestionProyectosAccess = () => {
  if (isAdminUser()) return true;
  const areas = getStoredAllowedAreas();
  const gestionArea = areas.find((area) => String(area?.slug || '').trim().toLowerCase() === 'gestion');
  const subareas = Array.isArray(gestionArea?.subareas) ? gestionArea.subareas : [];
  return subareas.includes(GESTION_PROYECTOS_SUBAREA_SLUG);
};

const dashboard = {
  id: 'group-dashboard',
  title: 'Navigation',
  type: 'group',
  children: [
    {
      id: 'operarios',
      title: 'Operarios',
      type: 'item',
      url: '/operarios',
      icon: icons.TeamOutlined,
      breadcrumbs: false,
      get hidden() {
        return !isOperariosContext() || !hasOperariosAccess();
      }
    },
    {
      id: 'produccion-operarios',
      title: 'Asignaciones operarios',
      type: 'item',
      url: '/produccion/asignaciones-operarios',
      icon: icons.TeamOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/produccion';
      }
    },
    {
      id: 'produccion-resumen-semanal',
      title: 'Resumen semanal',
      type: 'item',
      url: '/produccion/resumen-semanal',
      icon: icons.AuditOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/produccion';
      }
    },
    {
      id: 'produccion-calendario',
      title: 'Calendario',
      type: 'item',
      url: '/produccion/calendario',
      icon: icons.CalendarOutlined,
      breadcrumbs: false,
      hidden: true
    },
    {
      id: 'admin-metrics',
      title: 'Métricas',
      type: 'item',
      url: '/admin/metrics',
      icon: icons.AuditOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(isAdminUser() && getAreaBase() === '/admin');
      }
    },
    {
      id: 'admin-calendar',
      title: 'Calendario',
      type: 'item',
      url: '/admin/calendario',
      icon: icons.CalendarOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(isAdminUser() && getAreaBase() === '/admin');
      }
    },
    {
      id: 'iso-documentacion',
      title: 'Documentación',
      type: 'item',
      url: '/areas/iso',
      icon: icons.FileExclamationOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/areas/iso';
      }
    },
    {
      id: 'plan-estrategico-documentacion',
      title: 'Documentación',
      type: 'item',
      url: '/areas/plan-estrategico/documentacion-plan-estrategico',
      icon: icons.FileExclamationOutlined,
      breadcrumbs: false,
      activeUrls: ['/areas/plan-estrategico', '/areas/plan-estrategico/documentacion-plan-estrategico'],
      get hidden() {
        return getAreaBase() !== '/areas/plan-estrategico';
      }
    },
    {
      id: 'reportes-inicio',
      title: 'Inicio',
      type: 'item',
      url: '/areas/reportes',
      icon: PostAddIcon,
      breadcrumbs: false,
      exact: true,
      get hidden() {
        return getAreaBase() !== '/areas/reportes';
      }
    },
    {
      id: 'reportes-reportes',
      title: 'Reportes',
      type: 'item',
      url: '/areas/reportes/reportes',
      icon: PostAddIcon,
      breadcrumbs: false,
      exact: true,
      get hidden() {
        return getAreaBase() !== '/areas/reportes';
      }
    },
    {
      id: 'reportes-enviados',
      title: 'Enviados',
      type: 'item',
      url: '/areas/reportes/enviados',
      icon: icons.CalendarOutlined,
      breadcrumbs: false,
      exact: true,
      get hidden() {
        return getAreaBase() !== '/areas/reportes';
      }
    },
    {
      id: 'presupuestos-home',
      title: 'Presupuestos',
      type: 'item',
      url: '/areas/presupuestos',
      icon: icons.PieChartOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/areas/presupuestos';
      }
    },
    {
      id: 'presupuestos-proyectos',
      title: 'Gestion de Proyectos',
      type: 'item',
      url: '/areas/presupuestos/gestion-proyectos',
      icon: icons.BuildOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/areas/presupuestos' || !hasGestionProyectosAccess();
      }
    },
    {
      id: 'gestion-obras',
      get title() {
        const base = getAreaBase();
        if (base === '/contabilidad') return 'Media factura cobro';
        if (base === '/produccion') return 'Producción';
        if (base === '/areas/agenda') return 'Agenda';
        if (base === '/areas/mantenimientos-periodicos') return 'Revisiones';
        if (base === '/areas/instrumentacion') return 'Instrumentación';
        if (base.startsWith('/familias/') && isPresupuestosEntryContext()) return 'Gestión de Proyectos';
        return 'Gestión de Obras';
      },
      type: 'item',
      get url() {
        const base = getAreaBase() || '/cuadros';
        if (base === '/contabilidad') return `${base}/dashboard/media-factura-cobro`;
        if (base === '/produccion') return `${base}/calendario`;
        if (base === '/areas/agenda') return `${base}/calendario`;
        if (base === '/areas/mantenimientos-periodicos') return `${base}/calendario`;
        if (base === '/areas/instrumentacion') return `${base}/calendario`;
        if ((base || '').startsWith('/familias/')) return `${base}/gestion-obras`;
        return `${base}/dashboard/gestion-obras`;
      },
      icon: icons.BuildOutlined,
      breadcrumbs: false,
      get hidden() {
        const base = getAreaBase();
        return !(
          base &&
          (base === '/cuadros' ||
            base === '/contabilidad' ||
            base === '/areas/agenda' ||
            base === '/areas/mantenimientos-periodicos' ||
            base === '/areas/instrumentacion' ||
            base.startsWith('/familias/'))
        );
      }
    },
    {
      id: 'gestion-proyectos-unidades-obra',
      title: 'Unidades de Obra',
      type: 'item',
      get url() {
        const base = getAreaBase();
        const familySlug = base.startsWith('/familias/') ? base.split('/')[2] || '' : '';
        return familySlug ? `/familias/${familySlug}/gestion-proyectos/unidades-obra` : '/familias/gestion-proyectos/unidades-obra';
      },
      icon: icons.BuildOutlined,
      breadcrumbs: false,
      activeUrls: ['/familias/:familySlug/gestion-proyectos/unidades-obra'],
      get hidden() {
        const path = normalizePathname(typeof window !== 'undefined' ? window.location?.pathname || '' : '');
        if (!path.startsWith('/familias/')) return true;
        return path.includes('/gestion-obras');
      }
    },
    {
      id: 'familias-pr-reportes',
      title: 'Reportes',
      type: 'item',
      url: '/familias/pr/reportes',
      icon: icons.CheckCircleOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/familias/pr';
      }
    },
    {
      id: 'contabilidad-media-factura-pago',
      title: 'Media factura pago',
      type: 'item',
      get url() {
        return '/contabilidad/dashboard/media-factura-pago';
      },
      icon: icons.FileExclamationOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/contabilidad';
      }
    },
    {
      id: 'contabilidad-pago-60',
      title: 'Pago 60 Auditoría',
      type: 'item',
      get url() {
        return '/contabilidad/dashboard/pago-60-auditoria';
      },
      icon: icons.FileExclamationOutlined,
      breadcrumbs: false,
      get hidden() {
        return getAreaBase() !== '/contabilidad';
      }
    },
    {
      id: 'oportunidades',
      title: 'Oportunidades',
      type: 'item',
      get url() {
        return `/i/dashboard/oportunidades`;
      },
      icon: icons.AuditOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'visitas',
      title: 'Visitas',
      type: 'item',
      url: '/i/dashboard/visitas',
      icon: icons.FileExclamationOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'presupuestos-oportunidades',
      title: 'Presupuestos',
      type: 'item',
      url: '/i/dashboard/presupuestos',
      icon: icons.PieChartOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'instalaciones',
      title: 'Instalaciones',
      type: 'item',
      url: '/i/dashboard/instalaciones',
      icon: icons.BuildOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'pendiente-boletin',
      title: 'Pendiente Boletín',
      type: 'item',
      get url() {
        return `/i/dashboard/pendiente-boletin`;
      },
      icon: icons.ShoppingCartOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'terminados',
      title: 'Terminados',
      type: 'item',
      get url() {
        return `/i/dashboard/terminados`;
      },
      icon: icons.CheckCircleOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'rechazadas',
      title: 'Rechazadas',
      type: 'item',
      get url() {
        return `/i/dashboard/rechazadas`;
      },
      icon: icons.CloseCircleOutlined,
      breadcrumbs: false,
      get hidden() {
        return !(getAreaBase() || '').startsWith('/i');
      }
    },
    {
      id: 'navigation-contactos',
      title: 'Contactos',
      type: 'item',
      url: '/areas/gestion/telefonos',
      icon: icons.ContactsOutlined,
      breadcrumbs: false
    },
    {
      id: 'navigation-agenda',
      title: 'Agenda',
      type: 'item',
      url: '/i/agenda',
      icon: icons.CalendarOutlined,
      breadcrumbs: false,
      onClick: () => {
        try {
          localStorage.setItem('current_area', 'agenda');
        } catch {}
      }
    }
  ]
};

dashboard.children = dashboard.children.filter(Boolean);

export default dashboard;
